/**
 * The in-app alert notification (Story 25, task 106).
 *
 * The email reaches the operator who is away; this reaches the one who is
 * signed in. A 60-second poll of the All-Clients alerts query with a
 * `raised_since` bound — the raise, never back-fill, the email's two locks
 * — against a last-seen watermark, and a pulsing beacon lower right with
 * the count of unseen raised alerts. No push channel: the poll pauses
 * while the tab is hidden (TanStack's focus manager) and re-checks the
 * moment it is visible again, so a background tab is at worst one look
 * stale. A 401 stops the poll silently — a background check never raises
 * an error surface.
 *
 * The watermark is client-side until task 94's platform user record
 * offers a server-side home: `localStorage`, per user (MDR's user id), bound to the
 * OIDC `auth_time` so a new sign-in starts fresh (nothing that predates
 * the session counts) while a reload inside the session keeps what was
 * already seen. A second browser therefore counts independently —
 * accepted for the MVP (design r19).
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from 'react-oidc-context';

import { listAllAlerts } from '@bluesphere/api-client';

import { useAccessToken } from '../auth/useAccessToken';
import { useTenant } from '../auth/useTenant';

/** One page: the SPA counts rows, and a cursor means "more than a page". */
const POLL_LIMIT = 10;
const POLL_MS = 60_000;
const CAP = 9;

interface StoredWatermark {
  v: 1;
  /** ISO-8601: alerts raised strictly after this are unseen. */
  t: string;
  /** The OIDC `auth_time` this watermark belongs to; a new sign-in resets. */
  s: number;
}

function storageKey(userId: string): string {
  return `bsmdr.alerts.watermark.${userId}`;
}

/** Best-effort storage: private windows and blocked site data read as absent. */
function loadWatermark(userId: string, authTime: number): string | null {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredWatermark;
    if (parsed.v === 1 && parsed.s === authTime && typeof parsed.t === 'string') return parsed.t;
    return null;
  } catch {
    return null;
  }
}

function storeWatermark(userId: string, authTime: number, t: string): void {
  try {
    const value: StoredWatermark = { v: 1, t, s: authTime };
    window.localStorage.setItem(storageKey(userId), JSON.stringify(value));
  } catch {
    // Nothing: the in-memory value still drives this tab.
  }
}

export interface NewAlerts {
  /** Unseen raised alerts; capped display is the component's business. */
  count: number;
  /** True past one page — render "9+". */
  capped: boolean;
  label: string;
  markSeen: () => void;
}

export function useNewAlerts(): NewAlerts {
  const auth = useAuth();
  const idToken = useAccessToken();
  // MDR's user id from `/me`, not the token's `sub`: one person, one mark,
  // whichever pool signed them in (design r16 "The SPA").
  const userId = useTenant().me?.user_id ?? '';
  const authTime = auth.user?.profile.auth_time ?? 0;

  // Initialized to now on the first look of a new session, so nothing that
  // predates sign-in counts; a reload keeps the stored mark.
  const [watermark, setWatermark] = useState<string | null>(null);
  useEffect(() => {
    if (!userId || !authTime) return;
    const stored = loadWatermark(userId, authTime);
    const mark = stored ?? new Date().toISOString();
    if (!stored) storeWatermark(userId, authTime, mark);
    setWatermark(mark);
  }, [userId, authTime]);

  const query = useQuery({
    queryKey: ['new-alerts', watermark],
    queryFn: () =>
      listAllAlerts(
        { status: 'active', raised_since: watermark ?? undefined, limit: POLL_LIMIT },
        idToken,
      ),
    enabled: Boolean(idToken && watermark),
    // One error — a 401 after the idle sign-out, an API blip — stops the
    // interval; the next focus re-checks anyway (`refetchOnWindowFocus`).
    refetchInterval: (q) => (q.state.status === 'error' ? false : POLL_MS),
    refetchOnWindowFocus: 'always',
    retry: false,
  });

  return useMemo(() => {
    const items = query.data?.items ?? [];
    const capped = Boolean(query.data?.next_cursor) || items.length > CAP;
    const count = query.isError ? 0 : items.length;
    return {
      count,
      capped,
      label: capped ? `${CAP}+` : String(count),
      markSeen: () => {
        const now = new Date().toISOString();
        if (userId && authTime) storeWatermark(userId, authTime, now);
        setWatermark(now);
      },
    };
  }, [query.data, query.isError, userId, authTime]);
}

/**
 * The beacon: fixed lower right, a navy disc with the count, one slow
 * opacity pulse — static under `prefers-reduced-motion`. Click (or Enter;
 * it is a button, 48 px) opens the All-Clients Alerts queue filtered to
 * active — `view=all`, so the Client selection is untouched — and marks
 * everything seen. Absent while there is nothing new.
 */
export function NewAlertsBeacon({ alerts }: { alerts: NewAlerts }) {
  const navigate = useNavigate();

  const open = () => {
    alerts.markSeen();
    void navigate({ to: '/alerts', search: { view: 'all' } });
  };

  return (
    <>
      {/* Announced politely even when the beacon itself is new to the DOM. */}
      <div role="status" aria-live="polite" className="sr-only">
        {alerts.count > 0
          ? `${alerts.label} new alert${alerts.count === 1 ? '' : 's'} raised`
          : ''}
      </div>
      {alerts.count > 0 && (
        <button
          type="button"
          onClick={open}
          aria-label={`${alerts.label} new alerts raised. Open the Alerts queue.`}
          className="fixed bottom-6 right-6 z-50 flex h-12 w-12 animate-pulse items-center justify-center rounded-full bg-bluesphere-primary text-base font-bold text-white shadow-lg ring-2 ring-white/40 hover:animate-none focus-visible:animate-none motion-reduce:animate-none"
        >
          {alerts.label}
        </button>
      )}
    </>
  );
}
